// pwa-install.js — one easy, cross-device way to install the app.
//
// Mounted once (from main.js), it shows a dismissible "Install aboo" banner and handles every mobile:
//   • Android / Chromium / desktop → the real browser install prompt (beforeinstallprompt).
//   • iOS Safari → there is NO prompt API, so the button opens short "Add to Home Screen" steps.
// It hides itself when the app is already installed (standalone), when it runs inside a Capacitor
// native shell, or when the user dismissed it (re-offered after 14 days).
//
// It also registers the service worker (production only) and, when a new version is deployed, shows a
// one-tap "Update" that swaps the worker and reloads once. See public/sw.js.

import { html, signal } from './t4.js';

const DISMISS_KEY = 'aboo.pwaInstallDismissedAt';
const DISMISS_DAYS = 14;

// ---- environment detection -------------------------------------------------
function isStandalone(){
  try {
    return window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: minimal-ui)').matches
        || window.navigator.standalone === true; // iOS Safari
  } catch { return false; }
}
function isCapacitor(){
  const c = window.Capacitor;
  return !!c && (typeof c.isNativePlatform === 'function' ? c.isNativePlatform() : true);
}
function isIOS(){
  const ua = navigator.userAgent || '';
  const iOSUA = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1; // iPad poses as Mac
  return iOSUA || iPadOS;
}
function recentlyDismissed(){
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at > 0 && (Date.now() - at) < DISMISS_DAYS * 864e5;
  } catch { return false; }
}

// ---- state -----------------------------------------------------------------
let deferredPrompt = null;                 // the stashed beforeinstallprompt event (Android/desktop)
const canPrompt = signal(false);           // a real install prompt is available
const dismissed = signal(recentlyDismissed());
const installed = signal(isStandalone());
const iosOpen = signal(false);             // the iOS instructions sheet is open
const updateReady = signal(false);         // a new service-worker version is waiting

const iosEligible = isIOS() && !isStandalone() && !isCapacitor();

function bannerVisible(){
  if(installed.value || dismissed.value || isCapacitor()) return false;
  return canPrompt.value || iosEligible;
}

async function doInstall(){
  if(deferredPrompt){
    const dp = deferredPrompt;
    deferredPrompt = null;
    canPrompt.value = false;
    try { dp.prompt(); await dp.userChoice; } catch { /* user dismissed the native sheet */ }
    return;
  }
  if(iosEligible){ iosOpen.value = true; }
}
function dismiss(){
  dismissed.value = true;
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

// ---- view (built once, appended to <body>) ---------------------------------
function mountUI(){
  if(document.getElementById('pwa-install-root')) return;

  const el = html`<div id="pwa-install-root">
    <div class=${() => 'pwa-banner' + (bannerVisible() ? ' show' : '')} role="dialog" aria-label="Install aboo">
      <img class="pwa-banner-icon" src="/brand/aboo-icon-192.png" alt="" width="40" height="40">
      <div class="pwa-banner-text">
        <strong>Install aboo</strong>
        <span>Add it to your home screen for a faster, full-screen app.</span>
      </div>
      <div class="pwa-banner-actions">
        <button type="button" class="btn btn-outline pwa-banner-later" @click=${dismiss}>Not now</button>
        <button type="button" class="btn btn-primary pwa-banner-install" @click=${doInstall}>Install</button>
      </div>
    </div>

    <div class=${() => 'pwa-ios-sheet' + (iosOpen.value ? ' show' : '')} @click=${(e) => { if(e.target.classList.contains('pwa-ios-sheet')) iosOpen.value = false; }}>
      <div class="pwa-ios-card" role="dialog" aria-label="Add aboo to your home screen">
        <img class="pwa-banner-icon" src="/brand/aboo-icon-192.png" alt="" width="44" height="44">
        <h3>Install aboo on your iPhone</h3>
        <ol class="pwa-ios-steps">
          <li>Tap the <strong>Share</strong> icon <span class="pwa-ios-glyph" aria-hidden="true">&#x2191;</span> in the Safari toolbar.</li>
          <li>Scroll down and choose <strong>Add to Home Screen</strong>.</li>
          <li>Tap <strong>Add</strong> — aboo appears on your home screen.</li>
        </ol>
        <button type="button" class="btn btn-primary" @click=${() => { iosOpen.value = false; dismiss(); }}>Got it</button>
      </div>
    </div>

    <div class=${() => 'pwa-update-toast' + (updateReady.value ? ' show' : '')} role="status">
      <span>A new version of aboo is ready.</span>
      <button type="button" class="btn btn-primary pwa-update-btn" @click=${applyUpdate}>Update</button>
    </div>
  </div>`;

  document.body.appendChild(el);
}

// ---- service worker + update flow ------------------------------------------
let waitingWorker = null;
function applyUpdate(){
  if(waitingWorker){ waitingWorker.postMessage({ type: 'SKIP_WAITING' }); }
  updateReady.value = false;
}
function registerServiceWorker(){
  if(!('serviceWorker' in navigator)) return;
  // Dev runs through Vite's HMR server; a worker there only gets in the way. Prod/preview only.
  if(!import.meta.env || !import.meta.env.PROD) return;

  navigator.serviceWorker.register('/sw.js').then((reg) => {
    if(reg.waiting && navigator.serviceWorker.controller){ waitingWorker = reg.waiting; updateReady.value = true; }
    reg.addEventListener('updatefound', () => {
      const sw = reg.installing;
      if(!sw) return;
      sw.addEventListener('statechange', () => {
        if(sw.state === 'installed' && navigator.serviceWorker.controller){ waitingWorker = sw; updateReady.value = true; }
      });
    });
  }).catch(() => {});

  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if(reloaded) return; reloaded = true; window.location.reload();
  });
}

// ---- public entry ----------------------------------------------------------
export function initPwaInstall(){
  if(typeof window === 'undefined' || typeof document === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();          // keep the mini-infobar from auto-showing; we drive our own button
    deferredPrompt = e;
    canPrompt.value = true;
  });
  window.addEventListener('appinstalled', () => {
    installed.value = true;
    deferredPrompt = null;
    canPrompt.value = false;
  });
  // If the display mode flips to standalone (installed & launched), hide the banner live.
  try {
    window.matchMedia('(display-mode: standalone)').addEventListener('change', (ev) => {
      if(ev.matches) installed.value = true;
    });
  } catch {}

  const mount = () => { mountUI(); registerServiceWorker(); };
  if(document.body) mount();
  else window.addEventListener('DOMContentLoaded', mount, { once: true });
}
